import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '@/pages/admin/components/AdminLayout';
import type { AdminMaterial } from '@/mocks/adminData';
import { getMaterial, listMaterialVersions } from '@/api/materialInfo';
import type { MaterialVersionItem } from '@/api/materialInfo';
import { formatDateOnly } from '@/utils/date';
import { displayVersionLabel } from '@/utils/materialVersion';
import { getTree } from '@/api/materialCategory';
import type { CategoryNode, SubCategoryNode, ModelNode } from '@/api/materialCategory';
import PdfViewer from '@/pages/library/components/PdfViewer';
import PptViewer from '@/pages/library/components/PptViewer';
import { WordViewer, ExcelViewer } from 'react-file-preview-auto';
import type { ExcelData } from 'react-file-preview-auto';
import * as XLSX from 'xlsx';
import * as pdfjsLib from 'pdfjs-dist';
import 'react-file-preview-auto/dist/index.css';

// react-file-preview-auto 会将 workerSrc 覆盖为 CDN 地址，此处重新设回本地文件
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

/** 在API分类树中按三级分类（型号）ID查找完整路径 */
function findCategoryByModel(tree: CategoryNode[], modelId: string): { primary: CategoryNode; sub: SubCategoryNode; model: ModelNode } | null {
  for (const primary of tree) {
    for (const sub of primary.subCategories) {
      const model = sub.models.find((m) => m.id === modelId);
      if (model) return { primary, sub, model };
    }
  }
  return null;
}

/** 在API分类树中按二级分类ID查找所属一级分类 */
function findPrimaryBySubId(tree: CategoryNode[], subId: string): CategoryNode | undefined {
  return tree.find((c) => c.subCategories.some((s) => s.id === subId));
}

function buildCategoryPath(tree: CategoryNode[], mat: AdminMaterial): string {
  if (mat.modelCategory) {
    const found = findCategoryByModel(tree, mat.modelCategory);
    if (found) return `${found.primary.label} › ${found.sub.label} › ${found.model.label}`;
  }
  const primary = findPrimaryBySubId(tree, mat.category);
  if (primary) {
    const sub = primary.subCategories.find((s) => s.id === mat.category);
    if (sub) return `${primary.label} › ${sub.label}`;
  }
  if (mat.primaryCategory) {
    const primaryNode = tree.find((c) => c.id === mat.primaryCategory);
    if (primaryNode) return `${primaryNode.label} › ${mat.category}`;
  }
  return mat.category;
}

function buildShortCategoryPath(tree: CategoryNode[], mat: AdminMaterial): string {
  if (mat.modelCategory) {
    const found = findCategoryByModel(tree, mat.modelCategory);
    if (found) return `${found.sub.label} / ${found.model.label}`;
  }
  const primary = findPrimaryBySubId(tree, mat.category);
  if (primary) {
    const sub = primary.subCategories.find((s) => s.id === mat.category);
    if (sub) return `${primary.label} / ${sub.label}`;
  }
  return mat.category;
}

function TypeBadge({ type }: { type: AdminMaterial['type'] }) {
  const iconMap: Record<string, string> = {
    Video: 'ri-video-line',
    PDF: 'ri-file-pdf-line',
    PPT: 'ri-file-ppt-line',
    Excel: 'ri-file-excel-line',
    Word: 'ri-file-word-line',
  };
  const icon = iconMap[type] ?? 'ri-file-line';

  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-gray-200 bg-gray-50 text-gray-600 whitespace-nowrap">
      <i className={icon}></i>
      {type}
    </span>
  );
}

function LevelBadge({ confidential }: { confidential?: boolean }) {
  if (confidential) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold border border-gray-300 bg-gray-100 text-gray-600 whitespace-nowrap">
        <i className="ri-lock-line text-xs"></i>机密
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold border border-gray-200 bg-gray-50 text-gray-500 whitespace-nowrap">
      <i className="ri-global-line text-xs"></i>普通
    </span>
  );
}

export default function MaterialDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [material, setMaterial] = useState<AdminMaterial | null>(null);
  const [loading, setLoading] = useState(true);
  const [tree, setTree] = useState<CategoryNode[]>([]);
  const [excelData, setExcelData] = useState<ExcelData | null>(null);
  const [excelLoading, setExcelLoading] = useState(false);
  const [versionExpanded, setVersionExpanded] = useState(false);
  const [versions, setVersions] = useState<MaterialVersionItem[]>([]);
  const [versionLoading, setVersionLoading] = useState(false);
  const [versionLoadError, setVersionLoadError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await getTree();
        setTree(data);
      } catch (err) {
        console.error('加载分类树失败', err);
      }
    })();
  }, []);

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    setVersions([]);
    setVersionExpanded(false);
    setVersionLoadError(null);
    setLoadError(null);
    (async () => {
      try {
        setLoading(true);
        const item = await getMaterial(id);
        const mat: AdminMaterial = {
          id: item.id,
          title: item.title,
          type: item.type as AdminMaterial['type'],
          pptType: item.pptType as AdminMaterial['pptType'],
          primaryCategory: item.primaryCategory,
          category: item.category,
          modelCategory: item.modelCategory,
          fileSize: item.fileSize,
          uploadDate: formatDateOnly(item.uploadDate),
          uploadedBy: item.uploadedBy,
          uploadedByName: item.uploadedByName,
          views: item.views,
          downloads: item.downloads,
          previewAllowed: item.previewAllowed,
          downloadAllowed: item.downloadAllowed,
          thumbnail: item.thumbnail,
          isVideo: item.isVideo,
          videoDuration: item.videoDuration,
          fileUrl: item.fileUrl,
          description: item.description,
          tags: item.tags,
          confidential: item.confidential,
          confidentialAgents: item.confidentialAgents,
          currentVersion: displayVersionLabel(item.currentVersion),
        };
        setMaterial(mat);
      } catch (err) {
        console.error('加载资料详情失败', err);
        setLoadError((err as Error).message || '加载资料详情失败');
        setMaterial(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // 加载 Excel 文件数据用于客户端渲染
  useEffect(() => {
    if (material?.type === 'Excel' && material.fileUrl) {
      setExcelLoading(true);
      setExcelData(null);
      fetch(material.fileUrl)
        .then((res) => res.arrayBuffer())
        .then((buffer) => {
          const workbook = XLSX.read(buffer, { type: 'array' });
          const sheetsData: ExcelData['sheetsData'] = {};
          workbook.SheetNames.forEach((sheetName) => {
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
            const maxLength = Math.max(...jsonData.map((row) => row?.length || 0), 0);
            sheetsData[sheetName] = {
              data: jsonData.map((row) => {
                const paddedRow = Array.isArray(row) ? [...row] : [];
                while (paddedRow.length < maxLength) paddedRow.push(null);
                return paddedRow;
              }),
              maxColumns: maxLength,
            };
          });
          setExcelData({
            sheets: workbook.SheetNames,
            currentSheet: workbook.SheetNames[0],
            sheetsData,
          });
        })
        .catch(() => {})
        .finally(() => setExcelLoading(false));
    } else {
      setExcelData(null);
    }
  }, [material?.fileUrl, material?.type]);

  const loadVersions = async () => {
    if (!id || versionLoading) return;
    try {
      setVersionLoading(true);
      setVersionLoadError(null);
      const result = await listMaterialVersions(id);
      setVersions(result.list);
    } catch (err) {
      console.error('加载历史版本失败', err);
      setVersionLoadError((err as Error).message || '加载历史版本失败');
      setVersions([]);
    } finally {
      setVersionLoading(false);
    }
  };

  const toggleVersionHistory = () => {
    const next = !versionExpanded;
    setVersionExpanded(next);
    if (next && versions.length === 0) {
      loadVersions();
    }
  };

  if (loading) {
    return (
      <AdminLayout title="加载中..." subtitle="">
        <div className="flex items-center justify-center py-32">
          <i className="ri-loader-4-line animate-spin text-2xl text-gray-400"></i>
          <span className="ml-2 text-sm text-gray-400">加载资料详情...</span>
        </div>
      </AdminLayout>
    );
  }

  if (!material) {
    return (
      <AdminLayout title="资源未找到" subtitle="">
        <div className="flex flex-col items-center justify-center py-32 text-gray-400">
          <i className="ri-file-search-line text-5xl mb-4"></i>
          <p className="text-base font-semibold text-gray-600 mb-1">资源不存在</p>
          <p className="text-sm mb-6">{loadError || '您查找的资源不存在或已被删除。'}</p>
          <button
            onClick={() => navigate('/admin/materials')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
            style={{ background: '#0069BD' }}
          >
            <i className="ri-arrow-left-line text-sm"></i>
            返回资料库
          </button>
        </div>
      </AdminLayout>
    );
  }

  const totalViews = material.views;
  const totalDownloads = material.downloads;
  const categoryPath = buildCategoryPath(tree, material);
  const shortPath = buildShortCategoryPath(tree, material);

  const downloadLabel = (() => {
    if (material.isVideo || !material.downloadAllowed) return '仅浏览';
    return '允许下载';
  })();

  const downloadLabelStyle = (() => {
    if (material.isVideo || !material.downloadAllowed) {
      return 'border border-gray-200 bg-gray-50 text-gray-500';
    }
    return 'border border-gray-200 bg-gray-50 text-gray-700';
  })();

  return (
    <AdminLayout
      title={material.title}
      subtitle={`${shortPath} · ${displayVersionLabel(material.currentVersion)} · 上传者：${material.uploadedByName || material.uploadedBy} · ${material.uploadDate}`}
    >
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors cursor-pointer mb-5 group"
      >
        <i className="ri-arrow-left-line text-base group-hover:-translate-x-0.5 transition-transform"></i>
        返回
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="relative w-full bg-gray-50" style={{ minHeight: '360px' }}>
              {material.isVideo && material.fileUrl ? (
                <div className="w-full bg-black flex items-center justify-center" style={{ minHeight: '360px', maxHeight: '600px' }}>
                  <video
                    src={material.fileUrl}
                    controls
                    controlsList="nodownload"
                    disablePictureInPicture
                    onContextMenu={(e) => e.preventDefault()}
                    className="max-w-full max-h-full object-contain"
                    style={{ maxHeight: '600px' }}
                  />
                </div>
              ) : material.type === 'PDF' && material.fileUrl ? (
                <div className="w-full" style={{ minHeight: '600px', height: '80vh' }}>
                  <PdfViewer url={material.fileUrl} />
                </div>
              ) : material.type === 'Word' && material.fileUrl ? (
                <div className="w-full" style={{ minHeight: '600px', height: '80vh' }}>
                  <WordViewer url={material.fileUrl} />
                </div>
              ) : material.type === 'Excel' && material.fileUrl ? (
                <div className="w-full" style={{ minHeight: '600px', height: '80vh' }}>
                  {excelLoading && (
                    <div className="flex items-center justify-center h-full bg-white" style={{ minHeight: '360px' }}>
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-3 border-[#0069BD] border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm text-gray-500">加载表格数据...</span>
                      </div>
                    </div>
                  )}
                  {!excelLoading && excelData && <ExcelViewer excelData={excelData} />}
                  {!excelLoading && !excelData && (
                    <div className="flex flex-col items-center justify-center h-full gap-3 bg-white" style={{ minHeight: '360px' }}>
                      <i className="ri-error-warning-line text-4xl text-amber-500" />
                      <span className="text-sm text-gray-600">无法加载 Excel 文件</span>
                    </div>
                  )}
                </div>
              ) : material.type === 'PPT' && material.fileUrl ? (
                <div className="w-full" style={{ minHeight: '600px', height: '80vh' }}>
                  <PptViewer url={material.fileUrl} />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-4" style={{ minHeight: '360px' }}>
                  <i className="ri-file-line text-6xl text-gray-300"></i>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-700">{material.title}</p>
                    <p className="text-xs text-gray-400 mt-1">{material.type} · {material.fileSize}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>

        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-bold text-gray-800 mb-4">数据统计</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-gray-800">{totalViews.toLocaleString()}</p>
                <p className="text-xs text-gray-400 mt-0.5 flex items-center justify-center gap-1">
                  <i className="ri-eye-line"></i>总浏览量
                </p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 text-center">
                {material.isVideo || !material.downloadAllowed ? (
                  <>
                    <p className="text-2xl font-bold text-gray-300">—</p>
                    <p className="text-xs text-gray-400 mt-0.5 flex items-center justify-center gap-1">
                      <i className="ri-lock-line"></i>下载次数
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-2xl font-bold text-gray-800">{totalDownloads.toLocaleString()}</p>
                    <p className="text-xs text-gray-400 mt-0.5 flex items-center justify-center gap-1">
                      <i className="ri-download-line"></i>下载次数
                    </p>
                  </>
                )}
              </div>
            </div>
            {!material.isVideo && material.downloadAllowed && totalViews > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                  <span>下载率</span>
                  <span className="font-semibold text-gray-700">{Math.min(Math.round((totalDownloads / totalViews) * 100), 100)}%</span>
                </div>
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gray-400 rounded-full"
                    style={{ width: `${Math.min((totalDownloads / totalViews) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-bold text-gray-800 mb-4">文件信息</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <TypeBadge type={material.type} />
                <LevelBadge confidential={material.confidential} />
                {material.videoDuration && (
                  <span className="text-xs text-gray-400">{material.videoDuration}</span>
                )}
              </div>
              <div className="space-y-2.5 pt-1">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs text-gray-400 shrink-0">文件大小</span>
                  <span className="text-xs font-semibold text-gray-700 text-right">{material.fileSize}</span>
                </div>
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs text-gray-400 shrink-0">当前版本</span>
                  <span className="text-xs font-semibold text-gray-700 text-right">{displayVersionLabel(material.currentVersion)}</span>
                </div>
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs text-gray-400 shrink-0">分类</span>
                  <span className="text-xs font-semibold text-gray-700 text-right leading-relaxed">{categoryPath}</span>
                </div>
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs text-gray-400 shrink-0">上传者</span>
                  <span className="text-xs font-semibold text-gray-700 text-right">{material.uploadedByName || material.uploadedBy}</span>
                </div>
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs text-gray-400 shrink-0">上传日期</span>
                  <span className="text-xs font-semibold text-gray-700 text-right">{material.uploadDate}</span>
                </div>
              </div>

              {versions.length > 1 || displayVersionLabel(material.currentVersion) !== 'v1' ? (
                <div className="pt-3 mt-3 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={toggleVersionHistory}
                    className="flex items-center justify-between w-full text-xs font-semibold text-gray-600 hover:text-gray-900 cursor-pointer"
                  >
                    <span>
                      <i className={`ri-arrow-${versionExpanded ? 'up' : 'down'}-s-line mr-1`}></i>
                      历史版本{versions.length > 0 ? `（${versions.length}）` : ''}
                    </span>
                    {versionLoading && <i className="ri-loader-4-line animate-spin text-gray-400"></i>}
                  </button>
                  {versionExpanded && (
                    <div className="mt-3 space-y-2">
                      {versions.map((ver) => (
                        <div
                          key={ver.versionNo}
                          className="flex items-start justify-between gap-2 text-xs py-2 px-2 rounded-lg bg-gray-50"
                        >
                          <div className="min-w-0">
                            <span className="font-semibold text-gray-800">{ver.versionNo}</span>
                            {ver.isCurrent && (
                              <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-600">当前</span>
                            )}
                            <p className="text-gray-500 mt-0.5 truncate">
                              {formatDateOnly(ver.uploadDate)} · {ver.uploadedByName || ver.uploadedBy} · {ver.fileSize}
                            </p>
                          </div>
                        </div>
                      ))}
                      {!versionLoading && versionLoadError && (
                        <p className="text-xs text-red-500">{versionLoadError}</p>
                      )}
                      {!versionLoading && !versionLoadError && versions.length === 0 && (
                        <p className="text-xs text-gray-400">暂无历史版本记录</p>
                      )}
                    </div>
                  )}
                  <p className="text-[11px] text-gray-400 mt-2 leading-relaxed">
                    手动变更版本号会新增历史记录；仅替换文件或封面不会变更版本号，也不会新增历史记录。
                  </p>
                </div>
              ) : null}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-bold text-gray-800 mb-4">权限设置</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="ri-download-line text-sm text-gray-400"></i>
                  <span className="text-sm text-gray-600">下载</span>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-md flex items-center gap-1 ${downloadLabelStyle}`}>
                  {downloadLabel === '仅浏览' && <i className="ri-eye-line text-xs"></i>}
                  {downloadLabel === '允许下载' && <i className="ri-download-line text-xs"></i>}
                  {downloadLabel}
                </span>
              </div>
              {material.confidential && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <i className="ri-shield-user-line text-sm text-gray-400"></i>
                    <span className="text-sm text-gray-600">授权代理商</span>
                  </div>
                  <span className="text-xs font-semibold text-gray-700">
                    {material.confidentialAgents?.length ?? 0} 人
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <button
              onClick={() => navigate(`/admin/materials/${id}/edit`)}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
              style={{ background: '#0069BD' }}
            >
              <i className="ri-edit-line text-sm"></i>
              编辑资源
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}